"use client";

import { UserCircleIcon, PencilIcon } from "@heroicons/react/24/outline";
import { updateCreatorImageAction } from "@/app/actions";
import { UploaderWrapper } from "./uploader-wrapper";
import { useTranslations } from "next-intl";
import Image from "next/image";

interface ProfileAvatarUploaderProps {
  creatorId: number;
  existingImageUrl?: string;
  className?: string;
}

export function ProfileAvatarUploader({
  creatorId,
  existingImageUrl,
  className = "",
}: Readonly<ProfileAvatarUploaderProps>) {
  const t = useTranslations("ProfileAvatarUploader");
  const onUpload = (url: string) =>
    updateCreatorImageAction(creatorId, url, "avatar");
  const onRemove = () => updateCreatorImageAction(creatorId, "", "avatar");

  return (
    <UploaderWrapper
      aspectRatio={1}
      existingImageUrl={existingImageUrl}
      onUpload={onUpload}
      onRemove={onRemove}
    >
      {({ currentImageUrl, openUploadModal }) => (
        <div className={className}>
          {currentImageUrl ? (
            <button
              onClick={openUploadModal}
              className="relative h-full w-full cursor-pointer overflow-hidden rounded-full border-3 border-zinc-600 shadow-lg transition-all duration-200 hover:border-zinc-400 hover:shadow-xl"
            >
              <Image
                src={currentImageUrl}
                alt={t("alt")}
                fill
                className="object-cover"
                sizes="128px"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-all duration-200 group-hover:bg-black/40">
                <PencilIcon className="h-6 w-6 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
              </div>
            </button>
          ) : (
            <button
              onClick={openUploadModal}
              className="relative flex h-full w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-full border-3 border-dashed border-zinc-300 bg-zinc-100 shadow-md transition-all duration-200 hover:border-zinc-400 hover:bg-zinc-200 hover:shadow-lg dark:border-zinc-600 dark:bg-zinc-700 dark:hover:border-zinc-500 dark:hover:bg-zinc-600"
            >
              <UserCircleIcon className="h-12 w-12 text-zinc-500 dark:text-zinc-400" />
              <span className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">
                {t("uploadAvatar")}
              </span>
            </button>
          )}
        </div>
      )}
    </UploaderWrapper>
  );
}
